import { JoyHotelQueryStringParameters } from '@shared/components/DiscountsProvider/hotels/HotelsDiscountsProvider';
import { useFeatureFlagsContext } from '@shared/core/featureFlags';
import { useRetainSupportedStaysParams } from '@shared/hooks/useRetainSupportedStaysParams';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

const ROOMBLOCK = 'roomblock';
const HOTEL_BOOKING = 'hotelbooking';

const HOTEL_BOOKING_ROUTE = `/:eventHandle/${HOTEL_BOOKING}`;
const HOTEL_BOOKING_ROUTES = {
  hotelbooking: HOTEL_BOOKING_ROUTE,
  roomblock: `${HOTEL_BOOKING_ROUTE}/${ROOMBLOCK}/:id`,
  partnerhotels: `${HOTEL_BOOKING_ROUTE}/partnerhotels/:hotelId`,
} as const;

export const useHotelBookingRoutePaths = () => {
  const { formatToUrl } = useFeatureFlagsContext();
  const buildPath = useEventCallback((eventHandle: string, routePath: string, id?: string) => formatToUrl(`/${eventHandle}/${HOTEL_BOOKING}/${routePath}${id ? `/${id}` : ''}`));
  return {
    buildPath,
    roomblock: ROOMBLOCK,
    hotelBooking: HOTEL_BOOKING,
    hotelBookingRoute: HOTEL_BOOKING_ROUTE,
    hotelBookingRoutes: HOTEL_BOOKING_ROUTES,
  };
};

export type SearchParams = Readonly<{
  checkInDate?: string;
  checkOutDate?: string;
  discountCode?: string;
  name?: string;
  placeId?: string;
  utm_campaign: string;
  utm_medium: 'web' | 'email' | 'text';
  utm_source: string;
  // Joy discount code for accommodations
  [JoyHotelQueryStringParameters.joyHotelDiscountCode]?: string;
}>;

export const createSearchParamsFromAccommodationsPage = (utmCampaign: string): SearchParams => {
  return { utm_medium: 'web', utm_source: 'website_accommodations_page', utm_campaign: utmCampaign };
};

export const useHotelRoutePaths = () => {
  const { formatToUrl } = useFeatureFlagsContext();
  const { retainSupportedStaysParams } = useRetainSupportedStaysParams();
  return {
    hotelSearch: {
      path: '/:eventHandle/hotel/search',
      buildRelativeUrl: (eventHandle: string, searchParams: SearchParams) => {
        const params = new URLSearchParams(searchParams);
        return formatToUrl(retainSupportedStaysParams(`/${eventHandle}/hotel/search?` + params.toString()));
      },
    },
    hotelDetails: {
      path: '/:eventHandle/hotel/:joyPlaceId?',
      buildRelativeUrl: (eventHandle: string, joyPlaceId: string, searchParams: SearchParams) => {
        const params = new URLSearchParams(searchParams);
        return formatToUrl(retainSupportedStaysParams(`/${eventHandle}/hotel/${joyPlaceId}?` + params.toString()));
      },
    },
    bookYourStay: {
      path: '/:eventHandle/book-your-stay',
      buildRelativeUrl: (eventHandle: string, searchParams: SearchParams) => {
        const params = new URLSearchParams(searchParams);
        return formatToUrl(retainSupportedStaysParams(`/${eventHandle}/book-your-stay?` + params.toString()));
      },
    },
    accommodationPlace: {
      path: '/:eventHandle/accommodation-place/:accommodationPlaceId?',
      buildRelativeUrl: (eventHandle: string, accommodationPlaceId: string, searchParams?: SearchParams) => {
        const params = new URLSearchParams(searchParams);
        return formatToUrl(retainSupportedStaysParams(`/${eventHandle}/accommodation-place/${accommodationPlaceId}?` + params.toString()));
      },
    },
    accommodationsPage: {
      path: '/:eventHandle/accommodations',
      buildRelativeUrl: (eventHandle: string, searchParams?: SearchParams) => {
        const params = new URLSearchParams(searchParams);
        return formatToUrl(retainSupportedStaysParams(`/${eventHandle}/accommodations?` + params.toString()));
      },
    },
  };
};
